import type { CSSProperties, JSX } from 'react';

/**
 * The landing's ambient ATMOSPHERE — the layer that makes the page feel as alive as the product without a
 * single extra Pixi canvas. It sits FIXED behind all content (so the same soft light drifts behind every
 * section as you scroll, which is what breaks the old "one dead navy tunnel" feel) and paints three things:
 *
 *   1. A slow amber+indigo AURORA — three heavily-blurred radial blobs drifting on the compositor. Amber is
 *      the brand; a single cool indigo blob is the controlled secondary accent that makes the warm CTAs pop.
 *   2. A scatter of floating MOTES — tiny warm specks rising like dust in a sunbeam. Cozy, barely-there life.
 *   3. A static GRAIN overlay — kills the visible banding the big dark gradients get on wide/cheap panels.
 *
 * PERFORMANCE: pure CSS animation (transform/opacity only → GPU compositor, no layout/paint, no JS ticker).
 * ACCESSIBILITY: purely decorative → `aria-hidden`, `pointer-events-none`; every animation is silenced under
 * `prefers-reduced-motion` (handled in index.css) so the aurora + motes freeze into a calm still frame.
 * SSR/PRERENDER: the motes are a fixed, hand-tuned set (no `Math.random`) so the prerendered "/" markup
 * matches the client exactly — no hydration mismatch, no layout shift.
 */

/** A stable, hand-scattered set of motes. Deterministic so prerender === client. */
const MOTES: { left: string; top: string; size: number; dur: string; delay: string; dx: string; op: number }[] = [
  { left: '8%', top: '72%', size: 2.5, dur: '17s', delay: '0s', dx: '14px', op: 0.5 },
  { left: '18%', top: '88%', size: 1.5, dur: '21s', delay: '3s', dx: '-10px', op: 0.4 },
  { left: '27%', top: '64%', size: 3, dur: '15s', delay: '6s', dx: '8px', op: 0.55 },
  { left: '39%', top: '92%', size: 2, dur: '19s', delay: '1.5s', dx: '16px', op: 0.45 },
  { left: '48%', top: '58%', size: 1.5, dur: '23s', delay: '8s', dx: '-12px', op: 0.35 },
  { left: '57%', top: '84%', size: 2.5, dur: '16s', delay: '4s', dx: '10px', op: 0.5 },
  { left: '66%', top: '70%', size: 2, dur: '20s', delay: '9s', dx: '-14px', op: 0.4 },
  { left: '74%', top: '90%', size: 3, dur: '14s', delay: '2s', dx: '12px', op: 0.55 },
  { left: '83%', top: '62%', size: 1.5, dur: '22s', delay: '6.5s', dx: '-8px', op: 0.35 },
  { left: '91%', top: '80%', size: 2.5, dur: '18s', delay: '10s', dx: '14px', op: 0.5 },
  { left: '13%', top: '54%', size: 2, dur: '24s', delay: '5s', dx: '10px', op: 0.4 },
  { left: '35%', top: '48%', size: 1.5, dur: '26s', delay: '11s', dx: '-10px', op: 0.3 },
  { left: '61%', top: '50%', size: 2, dur: '25s', delay: '7.5s', dx: '12px', op: 0.35 },
  { left: '88%', top: '46%', size: 2.5, dur: '20s', delay: '12s', dx: '-12px', op: 0.4 },
];

export function LandingAtmosphere(): JSX.Element {
  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
      {/* ── Aurora: two warm amber blobs + one cool indigo accent, heavily blurred and slowly drifting. This
             is the CONSTANT ambient wash (fixed) — per-section glows add the changing warm/cool rhythm. ── */}
      <div
        className="ddo-aurora-a absolute -left-[14%] -top-[16%] h-[78vh] w-[78vh] rounded-full opacity-80 blur-[90px]"
        style={{ background: 'radial-gradient(circle, rgba(255,196,54,0.26), transparent 64%)' }}
      />
      <div
        className="ddo-aurora-b absolute -right-[14%] top-[20%] h-[72vh] w-[72vh] rounded-full opacity-70 blur-[100px]"
        style={{ background: 'radial-gradient(circle, rgba(112,120,248,0.22), transparent 64%)' }}
      />
      <div
        className="ddo-aurora-c absolute -bottom-[16%] left-[24%] h-[64vh] w-[64vh] rounded-full opacity-60 blur-[110px]"
        style={{ background: 'radial-gradient(circle, rgba(255,150,60,0.20), transparent 62%)' }}
      />

      {/* ── Motes: warm dust rising slowly. ── */}
      {MOTES.map((m, i) => (
        <span
          key={i}
          className="ddo-mote absolute rounded-full bg-amber-200"
          style={
            {
              left: m.left,
              top: m.top,
              width: m.size,
              height: m.size,
              filter: 'blur(0.5px)',
              '--mote-dur': m.dur,
              '--mote-dx': m.dx,
              '--mote-opacity': m.op,
              animationDelay: m.delay,
            } as CSSProperties
          }
        />
      ))}

      {/* ── Grain: static noise, soft-light blended, breaks banding on the dark washes. ── */}
      <div className="ddo-grain absolute inset-0 opacity-[0.045] mix-blend-soft-light" />
    </div>
  );
}
